import {
  required,
  email,
  min,
  max,
  min_value,
  max_value,
  numeric,
  integer,
  regex,
  confirmed,
} from "@vee-validate/rules";
import { Field, Form, ErrorMessage, defineRule, configure } from "vee-validate";
import es from "@vee-validate/i18n/dist/locale/es.json";
import en from "@vee-validate/i18n/dist/locale/en.json";
import { localize } from "@vee-validate/i18n";
import { effectScope, watch } from "vue";
import i18n from "@/plugins/i18n";
import { App } from "vue";

defineRule("email", email);
defineRule("max", max);
defineRule("min", min);
defineRule("numeric", numeric);
defineRule("integer", integer);
defineRule("required", required);
defineRule("required_trimmed", (value: unknown) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return !!value;
});
defineRule("min_value", min_value);
defineRule("max_value", max_value);
defineRule("regex", regex);
defineRule("confirmed", confirmed);
defineRule("password_uppercase", (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  return /[A-Z]/.test(value) || i18n.global.t("validation.password-uppercase");
});
defineRule("password_lowercase", (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  return /[a-z]/.test(value) || i18n.global.t("validation.password-lowercase");
});
defineRule("password_number", (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  return /[0-9]/.test(value) || i18n.global.t("validation.password-number");
});
defineRule("password_special", (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  return /[^a-zA-Z0-9\s]/.test(value) || i18n.global.t("validation.password-special");
});
defineRule("phone_number", (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  const normalized = value.replace(/[\s()-]/g, "");
  return /^\+?[1-9][0-9]{7,14}$/.test(normalized) || i18n.global.t("validation.phone-number");
});
defineRule("username_alphanumeric", (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  return /^[a-zA-Z0-9]+$/.test(value) || i18n.global.t("validation.username-alphanumeric");
});

localize({ es, en });
localize(i18n.global.locale.value as string);

const localeScope = effectScope();
localeScope.run(() => {
  watch(
    () => i18n.global.locale.value,
    (value) => {
      localize(value as string);
    }
  );
});

configure({
  generateMessage: (ctx) => {
    const rawField = String(ctx.field || "");
    const kebabField = rawField
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/_/g, "-")
      .toLowerCase();

    const candidateKeys = [rawField, kebabField].filter(Boolean) as string[];
    const fieldKey = candidateKeys.find((key) => i18n.global.te(key)) || rawField;
    const fieldName = i18n.global.t(fieldKey).toLowerCase();

    const currentLocale = i18n.global.locale.value as string;
    const message = localize(currentLocale)({
      ...ctx,
      name: fieldName,
    });

    return message;
  },
  validateOnInput: true,
});

const veeValidatePlugin = {
  install(app: App) {
    app.component("Form", Form);
    app.component("Field", Field);
    app.component("ErrorMessage", ErrorMessage);
  },
};

export default veeValidatePlugin;
